"use client";

import { SelectedFilterCount } from "@/components/common/selected-filter-count";
import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { BusinessStatusFilter } from "@/app/(saas)/(app)/businesses/_components/filters/status-filter";
import { DetailsFilter } from "@/app/(saas)/(app)/businesses/_components/filters/details-filter";
import { FinancingFilter } from "@/app/(saas)/(app)/businesses/_components/filters/financing-filter";
import { PriceAndFinancialsFilter } from "@/app/(saas)/(app)/businesses/_components/filters/price-and-financials-filter";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAtom } from "jotai";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";

type MoreFilterSection =
  | "status"
  | "financials"
  | "multiples"
  | "dealTerms"
  | "details"
  | "financing";

export function MoreFiltersPopover() {
  const [filters, setFilters] = useAtom(businessFiltersAtom);
  const [activeSection, setActiveSection] = useState<MoreFilterSection>("status");

  const filtersCount = useMemo(() => {
    let count = 0;
    count += filters.statuses.length;
    count += filters.listedBy.length;
    if (filters.revenueRange.min !== null || filters.revenueRange.max !== null) count++;
    if (filters.profitRange.min !== null || filters.profitRange.max !== null) count++;
    if (filters.sdeRange.min !== null || filters.sdeRange.max !== null) count++;
    if (filters.sdeMultipleRange.min !== null || filters.sdeMultipleRange.max !== null) count++;
    if (filters.revenueMultipleRange.min !== null || filters.revenueMultipleRange.max !== null) count++;
    if (filters.downPaymentRange.min !== null || filters.downPaymentRange.max !== null) count++;
    if (!filters.includeMissingMultiples) count++;
    if (filters.establishmentAgeRange.min !== null || filters.establishmentAgeRange.max !== null) count++;
    if (filters.employeeCountRange.min !== null || filters.employeeCountRange.max !== null) count++;
    if (filters.ownerWorkedHoursRange.min !== null || filters.ownerWorkedHoursRange.max !== null) count++;
    if (filters.propertyIncluded) count++;
    if (filters.financingAvailable) count++;
    if (filters.sbaPrequalified) count++;
    if (filters.visaQualified) count++;
    return count;
  }, [filters]);

  const sectionCounts = useMemo(() => ({
    status: filters.statuses.length,
    financials: [
      filters.revenueRange,
      filters.profitRange,
      filters.sdeRange,
    ].filter((r) => r.min !== null || r.max !== null).length,
    multiples: [
      filters.sdeMultipleRange,
      filters.revenueMultipleRange,
    ].filter((r) => r.min !== null || r.max !== null).length
      + (!filters.includeMissingMultiples ? 1 : 0),
    dealTerms:
      filters.downPaymentRange.min !== null ||
      filters.downPaymentRange.max !== null
        ? 1
        : 0,
    details: [
      filters.establishmentAgeRange,
      filters.employeeCountRange,
      filters.ownerWorkedHoursRange,
    ].filter((r) => r?.min !== null || r?.max !== null).length
      + (filters.propertyIncluded ? 1 : 0)
      + filters.listedBy.length,
    financing: [
      filters.financingAvailable,
      filters.sbaPrequalified,
      filters.visaQualified,
    ].filter(Boolean).length,
  }), [filters]);

  function resetAll() {
    setFilters((prev) => ({
      ...prev,
      statuses: [],
      listedBy: [],
      revenueRange: { min: null, max: null },
      profitRange: { min: null, max: null },
      sdeRange: { min: null, max: null },
      sdeMultipleRange: { min: null, max: null },
      revenueMultipleRange: { min: null, max: null },
      downPaymentRange: { min: null, max: null },
      establishmentAgeRange: { min: null, max: null },
      employeeCountRange: { min: null, max: null },
      ownerWorkedHoursRange: { min: null, max: null },
      propertyIncluded: null,
      financingAvailable: null,
      sbaPrequalified: null,
      visaQualified: null,
      includeMissingMultiples: true,
    }));
  }

  const sections: { key: MoreFilterSection; label: string }[] = [
    { key: "status", label: "Status" },
    { key: "financials", label: "Revenue & Profit" },
    { key: "multiples", label: "Valuation Multiples" },
    { key: "dealTerms", label: "Deal Terms" },
    { key: "details", label: "Business Details" },
    { key: "financing", label: "Financing" },
  ];

  const activeSectionLabel =
    sections.find((section) => section.key === activeSection)?.label ?? "Filters";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="min-w-0 h-full justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {filtersCount > 0 && <SelectedFilterCount count={filtersCount} />}
            <span className="overflow-hidden text-ellipsis">More Filters</span>
          </div>
          <Icon icon="ChevronDown" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-[520px] overflow-hidden rounded-xl p-0"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="text-sm font-medium">More filters</div>
          {filtersCount > 0 && (
            <button
              type="button"
              onClick={resetAll}
              className="text-xs text-muted-foreground transition-colors hover:text-foreground hover:underline"
            >
              Reset all
            </button>
          )}
        </div>

        <div className="flex max-h-[70vh] min-h-[260px]">
          <div
            role="tablist"
            aria-label="More filter sections"
            className="w-[180px] shrink-0 border-r py-2"
          >
            {sections.map((section) => (
              <button
                key={section.key}
                type="button"
                onClick={() => setActiveSection(section.key)}
                role="tab"
                id={`more-filter-tab-${section.key}`}
                aria-selected={activeSection === section.key}
                aria-controls={`more-filter-panel-${section.key}`}
                className={cn(
                  "flex min-h-11 md:min-h-10 w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors",
                  activeSection === section.key
                    ? "bg-muted text-foreground font-medium"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                )}
              >
                {section.label}
                {sectionCounts[section.key] > 0 && (
                  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-bold leading-none text-background">
                    {sectionCounts[section.key]}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div
            role="tabpanel"
            id={`more-filter-panel-${activeSection}`}
            aria-labelledby={`more-filter-tab-${activeSection}`}
            className="flex min-h-0 flex-1 flex-col p-4"
          >
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              {activeSectionLabel}
            </p>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {activeSection === "status" && <BusinessStatusFilter />}
              {activeSection === "financials" && <FinancialsSubFilter />}
              {activeSection === "multiples" && <MultiplesSubFilter />}
              {activeSection === "dealTerms" && <DealTermsSubFilter />}
              {activeSection === "details" && <DetailsFilter />}
              {activeSection === "financing" && <FinancingFilter />}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FinancialsSubFilter() {
  return (
    <div className="flex flex-col gap-4">
      <PriceAndFinancialsFilter mode="financials" />
    </div>
  );
}

function MultiplesSubFilter() {
  return (
    <div className="flex flex-col gap-4">
      <PriceAndFinancialsFilter mode="multiples" />
    </div>
  );
}

function DealTermsSubFilter() {
  return (
    <div className="flex flex-col gap-4">
      <PriceAndFinancialsFilter mode="dealTerms" />
    </div>
  );
}
